function titleCaseWord(word: string, mode: 'strict' | 'live' = 'strict'): string {
  return word
    .split(/([-'])/)
    .map((part) => {
      if (part === '-' || part === "'") return part;
      if (!part) return part;
      const first = part.charAt(0).toUpperCase();
      const rest =
        mode === 'strict' ? part.slice(1).toLowerCase() : part.slice(1);
      return first + rest;
    })
    .join('');
}

/** Capitalize the first letter of each word (names, cities, institutions). */
export function capitalizeWords(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  return trimmed.split(/\s+/).map(titleCaseWord).join(' ');
}

/**
 * Title-case words as the user types (preserves spaces).
 * Only uppercases the first letter of each word; Shift/Caps Lock casing in the rest is kept.
 *
 * Used by: DraftEditor title, CityAutocompleteInput, formatTypedProfileTextFieldLive
 * (Onboarding + Settings profile fields).
 */
export function capitalizeWordsLive(value: string): string {
  if (!value) return '';
  return value.replace(/\S+/g, (word) => titleCaseWord(word, 'live'));
}

/** Trim and apply live word caps (for onBlur; does not force the rest of each word lowercase). */
export function capitalizeWordsOnBlur(value: string): string {
  const trimmed = value.trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  return capitalizeWordsLive(trimmed);
}

export type TypedProfileTextField =
  | 'fullName'
  | 'lawCollege'
  | 'city'
  | 'firmName'
  | 'barRegNumber'
  | 'firmAffiliation'
  | 'courtStation';

export function formatTypedProfileTextField(
  field: TypedProfileTextField,
  value: string,
): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (field === 'barRegNumber') {
    return trimmed.toUpperCase();
  }

  return capitalizeWords(trimmed);
}

export function formatTypedProfileTextFieldLive(
  field: TypedProfileTextField,
  value: string,
): string {
  if (!value) return '';

  if (field === 'barRegNumber') {
    return value.toUpperCase();
  }

  return capitalizeWordsLive(value);
}

/** Profile field blur: trim and cap word starts without undoing Shift/Caps Lock typing. */
export function formatTypedProfileTextFieldBlur(
  field: TypedProfileTextField,
  value: string,
): string {
  if (!value.trim()) return '';

  if (field === 'barRegNumber') {
    return value.trim().toUpperCase();
  }

  return capitalizeWordsOnBlur(value);
}
